/* eslint-disable max-lines-per-function */
import { ChevronDownIcon, DownloadIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  useToast
} from "@chakra-ui/react"
import { useMemo, useState } from "react"
import bg from "../assets/images/uniqueSolutions.png"
import {
  breadCrumbFirstLevel,
  breadCrumbReportsAndResourcesSecondLevel,
  pageContentWidth
} from "../common/commonConstants"
import { buildDocumentUrl } from "../common/documentUrl"
import { BreadCrumb } from "../components/commons/breadcrumb"
import { PDFIcon } from "../components/productInfo/product-info.main"
import { StaticPageHeader } from "../components/commons/static-page-header"
import { Tabs } from "../components/tabs"
import { Colors } from "../theme/colors"
import { SubPage, SubPageContent } from "../components/SubPage"
import { useGetReportLazyQuery, useReportPageDetailsQuery } from "../type-and-hooks"

const RESTRICTED_REPORT_MESSAGE = "* Please contact our customer support for the report."

interface UniqueRow {
  description: string
  frl: string
  report: string
  ref: string
}

interface UniqueSection {
  title: string
  rows: UniqueRow[]
}

const UNIQUE_DATA: UniqueSection[] = [
  {
    title: "Acoustic Lagging on Pipes",
    rows: [
      { description: "Up to 100mm PVC stack lagged in Soundlag 4525C or Thermotec NuWrap 5 to base of cast-in collar", frl: "-/120/120", report: "FCO3426", ref: "Section 3.3, e." },
      { description: "Up to 100mm PVC floorwaste lagged in Soundlag 4525C or Thermotec NuWrap 5 to base of cast-in collar", frl: "-/120/120", report: "FCO3426", ref: "Section 3.3, e." },
      { description: "Up to 160mm HDPE stack lagged in Soundlag 4525C or Thermotec NuWrap 5 to base of cast-in collar", frl: "-/120/120", report: "FCO3426", ref: "Section 3.3, f." },
      { description: "50mm HDPE floorwaste lagged in Soundlag 4525C or Thermotec NuWrap 5 to base of cast-in collar", frl: "-/120/120", report: "FCO3426", ref: "Section 3.3, Table 17a" },
      { description: "150mm PVC floorwaste through a H150FWS completely lagged to the base of the collar in Soundlag 4525C", frl: "-/240/180", report: "FSP2264", ref: "5" },
      { description: "100mm PVC 4-way riser floorwaste through a H100FWS completely lagged to the base of the collar in Soundlag 4525C", frl: "-/120/120", report: "FSP2028", ref: "5" },
      { description: "100mm PVC floorwaste through a H100FWS completely lagged to the base of the collar in Thermotec NuWrap 5", frl: "-/240/240", report: "FR5670††", ref: "2" },
      { description: "50mm PVC floorwaste through a H50FWS completely lagged to the base of the collar in Thermotec NuWrap 5", frl: "-/240/240", report: "FR5670††", ref: "3" },
      { description: "150mm PVC stack through a H150S completely lagged to the base of the collar in Soundlag 4525C", frl: "-/240/180", report: "FSP2264", ref: "1" }
    ]
  },
  {
    title: "Beer Python (Beverage Python)",
    rows: [
      { description: "130mm thick beer python (12 x 12mm & 8 x 16mm tubes) with 25mm thick foam insulation through 150mm concrete slab and HP150R", frl: "-/180/180", report: "FSP2298", ref: "3" }
    ]
  },
  {
    title: "Collars in a Cluster",
    rows: [
      { description: "Up to 160mm stack cast-in collars in a 2x10 cluster", frl: "-/120/120", report: "FCO3426", ref: "Section 3.3, b." },
      { description: "20 x H110S in a 2x10 cluster", frl: "-/120/120", report: "FCO3373", ref: "—" },
      { description: "2 x H100S, H150S & a H50S in a cluster", frl: "-/240/120", report: "FSP1741", ref: "1" }
    ]
  },
  {
    title: "Collars in the Side of a Beam",
    rows: [
      { description: "Up to 160mm plastic pipe services, stack or floorwaste, exiting the side of a beam through a cast-in collar", frl: "-/120/120", report: "FCO3426", ref: "Section 3.2, f., ii." }
    ]
  },
  {
    title: "Collar Recessed into the Slab",
    rows: [
      { description: "Cast-in collar systems recessed 35mm into a slab", frl: "-/120/120", report: "FCO3426", ref: "Section 3.3, d." },
      { description: "H100FWS recessed 50mm into the slab protecting a 100 PVC floorwaste", frl: "-/240/240", report: "FR5670††", ref: "22" }
    ]
  },
  {
    title: "Floorwaste Retrofit Under Stack Cast-in",
    rows: [
      { description: "100mm PVC floorwaste with H100S cast-in a 150mm concrete slab with a LP100R mounted underneath", frl: "-/240/240", report: "FSP1673†", ref: "4" },
      { description: "50mm PVC floorwaste with H50S cast-in a 150mm concrete slab with a LP50R mounted underneath", frl: "-/240/240", report: "FSP1673†", ref: "3" },
      { description: "100 PVC floorwaste and 100 Promat stack collar with repair plate and LP100R-D", frl: "-/240/180", report: "FSP2271", ref: "1" }
    ]
  },
  {
    title: "Fuseal PP110 Pipe Installations",
    rows: [
      { description: "110 Fuseal Floor Waste in a 180mm+ concrete slab through H100FWS w/ LP100R-D underneath", frl: "-/240/240", report: "FSP2274", ref: "1" }
    ]
  },
  {
    title: "Gerberit SuperTube and SoVent",
    rows: [
      { description: "SuperTube BottomTurn Bend pushed all the way up into a 110R", frl: "-/240/180", report: "FSP2116", ref: "5" },
      { description: "SuperTube BottomTurn Bend pushed all the way up into a H100S", frl: "-/240/180", report: "FSP2116", ref: "4" },
      { description: "SuperTube and Sovent Fitting used anywhere in the HP100R, 110R, H100S, L100FWS, H110S & H150S", frl: "-/240/180", report: "FCO3396", ref: "—" }
    ]
  },
  {
    title: "HDPE Butt Welds",
    rows: [
      { description: "HDPE butt welds to be located anywhere within the HP100R, 110R, H100S, L100FWS, H110S & H150S", frl: "-/240/180", report: "FCO3396", ref: "—" }
    ]
  },
  {
    title: "Paint on Collars",
    rows: [
      { description: "Base flange of cast-in collars (Up to 150 size) coated in paint", frl: "-/120/120", report: "FCO3426", ref: "Section 3.2, e., iii." },
      { description: "Up to 150mm PVC stack with a thin sprayed layer of Taubmans Easycoat ceiling white paint or Taubmans ceiling premium white paint on collar shell and intumescent", frl: "-/120/120", report: "FCO3426", ref: "Section 3.3, e." }
    ]
  },
  {
    title: "PEX Pipe Bundles",
    rows: [
      { description: "Bundle of 3 x 25mm PEX-b pipes through an H50FWS cast-in collar", frl: "-/120/120", report: "FSP1872", ref: "2" },
      { description: "Bundle of 4 x 20mm PEX-b pipes through an H50FWS cast-in collar", frl: "-/120/120", report: "FSP1872", ref: "3" },
      { description: "Bundle of 3 x 20mm PEX-a pipes through an LP50R collar under a concrete slab", frl: "-/240/240", report: "FSP1849", ref: "2" },
      { description: "Bundle of 2 x 20mm PEX-b pipes through an 32R collar on a 2x13mm plasterboard wall", frl: "-/120/120", report: "FSP1902", ref: "6" },
      { description: "Bundle of 20mm PEX-b pipe & 20mm PEX-Al-PEX pipe through an 32R collar on a 2x13mm plasterboard wall", frl: "-/120/120", report: "FSP1902", ref: "9" },
      { description: "Bundle of 20mm PEX-b pipe & 25mm PEX-Al-PEX pipe through an 50R collar on a 2x13mm plasterboard wall", frl: "-/120/120", report: "FSP1902", ref: "4" }
    ]
  },
  {
    title: "Unused Collars",
    rows: [
      { description: "50 to 100 cast-in collars blanked off with a PVC plug", frl: "-/240/120", report: "FCO3426", ref: "Section 3.2, d., ii." },
      { description: "50 to 150 cast-in collars blanked off with GAL plate and filled with concrete (Installation Instructions)", frl: "-/240/240", report: "FCO3426", ref: "Section 3.2, d., i." },
      { description: "H150S blanked off with 150mm PVC plug", frl: "-/240/180", report: "FSP2271", ref: "2" },
      { description: "H100FWS blanked off with GAL plate and filled with concrete (Installation Instructions)", frl: "-/240/240", report: "FR5670††", ref: "20" },
      { description: "H110S blanked off with 100mm PVC plug", frl: "-/120/120", report: "FSP2153", ref: "1" },
      { description: "H100S blanked off with 100mm PVC plug", frl: "-/120/120", report: "FSP2153", ref: "4" },
      { description: "H65S blanked off with 65mm PVC plug", frl: "-/120/120", report: "FSP2153", ref: "2" },
      { description: "H50S blanked off with 50mm PVC plug", frl: "-/120/120", report: "FSP2153", ref: "3" }
    ]
  },
  {
    title: "Unique Elements — Permanent Metal Deck Slabs",
    rows: [
      { description: "Cast-in collars systems on Bondeck", frl: "-/120/120", report: "FCO3426", ref: "Section 3.3, a." },
      { description: "Cast-in collars systems on other steel permanent formwork", frl: "-/120/120", report: "FCO3426", ref: "Section 3.3, a." }
    ]
  },
  {
    title: "Unique Elements — Shaft Liner Walls",
    rows: [
      { description: "Shaft liner walls", frl: "-/120/120", report: "FCO3348", ref: "—" }
    ]
  },
  {
    title: "Unique Elements — MgO Board Walls",
    rows: [
      { description: "FireCrunch wall systems with SNAP retrofit collars", frl: "-/90/90", report: "FCO3268", ref: "—" },
      { description: "Multiboard wall systems with SNAP retrofit collars", frl: "-/90/90", report: "FCO3369", ref: "—" }
    ]
  },
  {
    title: "Plasterboard Ceiling with RSIF 60min",
    rows: [
      { description: "100mm PVC stack through a LP100R-D on a plasterboard ceiling", frl: "-/90/90", report: "FSP2097", ref: "2" },
      { description: "100mm PVC floorwaste through a LP100R-D on a plasterboard ceiling", frl: "-/90/90", report: "FSP2097", ref: "1" },
      { description: "50mm PVC floorwaste through a LP50R on a plasterboard ceiling", frl: "-/90/90", report: "FSP2097", ref: "3" },
      { description: "110mm Raupiano stack through a LP100R-D on a plasterboard ceiling", frl: "-/90/90", report: "FSP1906", ref: "1" },
      { description: "110mm Raupiano floorwaste through a LP100R-D on a plasterboard ceiling", frl: "-/90/90", report: "FSP1906", ref: "2" },
      { description: "50mm Raupiano stack through a LP50R on a plasterboard ceiling", frl: "-/90/90", report: "FSP1906", ref: "3" },
      { description: "20mm PEX-a stack through a 32R on a plasterboard ceiling", frl: "-/90/90", report: "FSP2097", ref: "4" },
      { description: "Range of SNAP retrofit collars covering various pipe materials", frl: "—", report: "—", ref: "—" }
    ]
  }
]

function parseReport(raw: string): { lookupName: string; footnote: string } {
  if (raw.endsWith("††")) return { lookupName: raw.slice(0, -2), footnote: "††" }
  if (raw.endsWith("†")) return { lookupName: raw.slice(0, -1), footnote: "†" }
  return { lookupName: raw, footnote: "" }
}

const COL = "55% 12% 13% 20%"
const HEADER_BG = "#AB8476"
const SECTION_BG = "#e8e8e8"
const ROW_HOVER = "#faf5f7"
const MAROON = "#861F41"

export const UniqueSolutions = (): JSX.Element => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const toast = useToast()
  const [getReport] = useGetReportLazyQuery({ fetchPolicy: 'network-only' })
  const { data: reportPageData } = useReportPageDetailsQuery({ fetchPolicy: "cache-and-network" })

  const reportPublicStatusByNo = useMemo(() => {
    const map = new Map<string, boolean>()
    reportPageData?.reportDetails?.forEach(r => {
      if (r?.reportNo) map.set(r.reportNo, r.isPublic === true)
    })
    return map
  }, [reportPageData])

  const hasRestrictedReport = useMemo(
    () =>
      UNIQUE_DATA.some(section =>
        section.rows.some(row => {
          if (row.report === "—") return false
          const { lookupName } = parseReport(row.report)
          return reportPublicStatusByNo.get(lookupName) === false
        })
      ),
    [reportPublicStatusByNo]
  )

  const toggleSection = (title: string): void => {
    setCollapsed(prev => ({ ...prev, [title]: !prev[title] }))
  }

  const downloadFromUrl = (url: string, name: string): void => {
    const link = document.createElement("a")
    link.href = url
    link.download = `${name}.pdf`
    link.target = "_blank"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast({ title: "Download started", status: "success", duration: 3000, isClosable: true })
  }

  const handleReportPreview = async (reportNo: string): Promise<void> => {
    setActionLoading(`preview-${reportNo}`)
    try {
      const { data } = await getReport({ variables: { reportNo } })
      const url = data?.getReport?.reportURL ?? null
      if (url) {
        window.open(buildDocumentUrl(data?.getReport?.fileName, url) ?? url, "_blank", "noopener,noreferrer")
        return
      }
      onOpen()
    } catch {
      onOpen()
    } finally {
      setActionLoading(null)
    }
  }

  const handleReportDownload = async (reportNo: string): Promise<void> => {
    setActionLoading(`download-${reportNo}`)
    try {
      const { data } = await getReport({ variables: { reportNo } })
      const url = data?.getReport?.downloadURL ?? data?.getReport?.reportURL ?? null
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer")
        toast({ title: "Download started", status: "success", duration: 3000, isClosable: true })
      } else {
        toast({ title: "Report not available", description: "Please contact SNAP Collars for assistance.", status: "error", duration: 4000, isClosable: true })
      }
    } catch {
      toast({ title: "Report not available", description: "Please contact SNAP Collars for assistance.", status: "error", duration: 4000, isClosable: true })
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <>
      <SubPage>
        <StaticPageHeader title="Unique Solutions " bg={bg} />
        <BreadCrumb
          options={{
            ...breadCrumbFirstLevel,
            ...breadCrumbReportsAndResourcesSecondLevel,
            thirdLevel: { title: "Unique Solutions" }
          }}
        />
        <SubPageContent innerBoxProps={{ w: ["100%", "100%", "100%", pageContentWidth, "80%", "75%"] }}>
          <Box mt="26px" px={[4, 4, 0]}>
            <Tabs tabs={["Unique Installation"]} defaultTabIndex={0} tabClickHandler={() => {}} />
            <Box mt="30px" overflowX="auto">
              <Box minW="620px">
                {/* Table header */}
                <Box
                  display="grid"
                  gridTemplateColumns={COL}
                  bg={HEADER_BG}
                  color="white"
                  fontSize="12px"
                  fontWeight="600"
                >
                  <Box px="12px" py="8px">Details</Box>
                  <Box px="12px" py="8px" textAlign="center">FRL</Box>
                  <Box px="12px" py="8px" textAlign="center">Report</Box>
                  <Box px="12px" py="8px" textAlign="center">Report Reference</Box>
                </Box>

                {/* Sections */}
                {UNIQUE_DATA.map(section => {
                  const isCollapsed = collapsed[section.title] ?? false
                  return (
                    <Box key={section.title}>
                      {/* Section header — full-width collapsible row */}
                      <Flex
                        align="center"
                        gap="8px"
                        px="12px"
                        py="7px"
                        bg={SECTION_BG}
                        fontWeight="500"
                        fontSize="13px"
                        cursor="pointer"
                        userSelect="none"
                        borderBottom="0.5px solid #f0f0f0"
                        onClick={() => { toggleSection(section.title) }}
                      >
                        <ChevronDownIcon
                          flexShrink={0}
                          transition="transform 0.2s"
                          transform={isCollapsed ? "rotate(-90deg)" : "rotate(0deg)"}
                        />
                        {section.title}
                      </Flex>

                      {/* Data rows */}
                      {!isCollapsed && section.rows.map((row, rowIndex) => {
                        const { lookupName, footnote } = parseReport(row.report)
                        const isRestricted = reportPublicStatusByNo.get(lookupName) === false
                        return (
                          <Box
                            key={rowIndex}
                            display="grid"
                            gridTemplateColumns={COL}
                            bg="white"
                            borderBottom="0.5px solid #f0f0f0"
                            _hover={{ bg: ROW_HOVER }}
                            fontSize="13px"
                          >
                            <Box px="12px" py="8px" lineHeight="1.4">{row.description}</Box>
                            <Box px="12px" py="8px" textAlign="center" fontFamily="monospace">{row.frl}</Box>
                            <Box px="12px" py="8px" textAlign="center">
                              {row.report === "—" ? (
                                <Text fontSize="13px" color="gray.400">—</Text>
                              ) : isRestricted ? (
                                <Flex align="center" justify="center" gap="4px" flexWrap="nowrap">
                                  <Box p="0" minW="0" h="auto" visibility="hidden" aria-hidden="true">
                                    <DownloadIcon />
                                  </Box>
                                  <Tooltip label={RESTRICTED_REPORT_MESSAGE} hasArrow placement="top">
                                    <Flex align="center" gap="4px" cursor="default">
                                      <PDFIcon />
                                      <Text as="span" fontSize="13px" color="black">
                                        {lookupName}
                                        {footnote && <sup style={{ fontSize: "10px" }}>{footnote}</sup>}
                                        {" *"}
                                      </Text>
                                    </Flex>
                                  </Tooltip>
                                </Flex>
                              ) : (
                                <Flex align="center" justify="center" gap="4px" flexWrap="nowrap">
                                  <Button
                                    variant="linkButton"
                                    p="0"
                                    minW="0"
                                    h="auto"
                                    display="flex"
                                    alignItems="center"
                                    flexShrink={0}
                                    color={MAROON}
                                    onClick={() => { void handleReportDownload(lookupName) }}
                                    isLoading={actionLoading === `download-${lookupName}`}
                                    aria-label={`Download ${lookupName}`}
                                  >
                                    <DownloadIcon />
                                  </Button>
                                  <Button
                                    variant="linkButton"
                                    p="0"
                                    minW="0"
                                    h="auto"
                                    display="flex"
                                    alignItems="center"
                                    gap="4px"
                                    color={MAROON}
                                    fontSize="13px"
                                    fontWeight="400"
                                    flexShrink={0}
                                    onClick={() => { void handleReportPreview(lookupName) }}
                                    isLoading={actionLoading === `preview-${lookupName}`}
                                  >
                                    <PDFIcon />
                                    <Text as="span" fontSize="13px">{lookupName}</Text>
                                    {footnote && <sup style={{ fontSize: "10px" }}>{footnote}</sup>}
                                  </Button>
                                </Flex>
                              )}
                            </Box>
                            <Box px="12px" py="8px" textAlign="center" fontSize="13px" color={row.ref === "—" ? "gray.400" : "inherit"}>
                              {row.ref}
                            </Box>
                          </Box>
                        )
                      })}
                    </Box>
                  )
                })}
              </Box>

              {/* Footnotes */}
              <Box mt="16px" px="2px">
                <Text fontSize="12px" color="gray.500">† Refer to report in conjunction with FCO3407</Text>
                <Text fontSize="12px" color="gray.500" mt="2px">†† Refer to report in conjunction with FC14077</Text>
                {hasRestrictedReport && (
                  <Text fontSize="12px" color={Colors.Secondary.c2} mt="2px">
                    {RESTRICTED_REPORT_MESSAGE}
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
        </SubPageContent>
      </SubPage>

      <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent h="90vh">
          <ModalHeader color={Colors.Primary.c1} borderBottom="1px solid" borderColor={Colors.Secondary.c2_300} pr="48px">
            Report Unavailable
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p="0">
            <Flex align="center" justify="center" h="100%" minH="70vh" flexDir="column" gap="12px" px="32px" textAlign="center">
              <Text fontSize="18px" color={Colors.Secondary.c2} fontFamily="PT Sans">
                This report is not currently available.
              </Text>
              <Text fontSize="16px" color={Colors.Secondary.c2} fontFamily="PT Sans">
                Please{" "}
                <Text as="span" color={Colors.Primary.accent} textDecoration="underline">
                  <a href="/contact-us">contact SNAP Collars</a>
                </Text>{" "}
                for assistance.
              </Text>
            </Flex>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor={Colors.Secondary.c2_300} justifyContent="flex-end" gap="8px">
            <Button
              bg="white"
              color={Colors.Primary.c1}
              border="2px solid"
              borderColor={Colors.Primary.c1}
              _hover={{ bg: Colors.Primary.c1, color: "white", borderColor: Colors.Primary.c1 }}
              onClick={onClose}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
